"use client";

import { LogoMark } from "@shared/components/Logo";
import { useRouterState } from "@tanstack/react-router";
import {
	Sheet,
	SheetContent,
	SheetTitle,
	SheetTrigger,
} from "@ui/components/sheet";
import { cn } from "@ui/lib";
import { MenuIcon } from "lucide-react";
import { useState } from "react";
import { NAV_LINKS } from "../site";
import { mkButton } from "./MkButton";

/**
 * A thin, flat bar: the mark, every page of the site, and one action. Frosted over the page as
 * it scrolls under. On phones the pages move into a full-height menu.
 */
export function MarketingHeader() {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	return (
		<header className="sticky top-0 z-40 border-(--mk-line)/70 border-b bg-(--mk-white)/80 backdrop-blur-xl backdrop-saturate-150">
			<div className="mk-container flex h-14 items-center gap-6">
				<a
					href="/"
					className="flex shrink-0 items-center gap-2 rounded-md font-semibold text-[15px] tracking-[-0.02em] outline-none focus-visible:ring-2 focus-visible:ring-(--mk-signal)"
				>
					<LogoMark className="size-6" />
					Hephon Commerce
				</a>
				<nav
					aria-label="Main"
					className="ms-auto hidden items-center gap-6 lg:flex"
				>
					{NAV_LINKS.map((link) => (
						<a
							key={link.href}
							href={link.href}
							aria-current={
								pathname === link.href ? "page" : undefined
							}
							className="text-[13px] text-(--mk-ink-2) outline-none transition-colors duration-(--duration-hover) hover:text-(--mk-ink) focus-visible:text-(--mk-ink) aria-[current=page]:text-(--mk-ink)"
						>
							{link.label}
						</a>
					))}
				</nav>
				<div className="ms-auto flex items-center gap-2 lg:ms-2">
					<a
						href="/login"
						className="hidden px-2 text-[13px] text-(--mk-ink-2) hover:text-(--mk-ink) sm:inline"
					>
						Sign in
					</a>
					<a href="/demo" className={mkButton({ size: "sm" })}>
						Book a demo
					</a>
					<MobileMenu pathname={pathname} />
				</div>
			</div>
		</header>
	);
}

function MobileMenu({ pathname }: { pathname: string }) {
	const [open, setOpen] = useState(false);
	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetTrigger
				aria-label="Open menu"
				className="-me-2 grid size-10 place-items-center rounded-full text-(--mk-ink) outline-none hover:bg-(--mk-mist) focus-visible:ring-2 focus-visible:ring-(--mk-signal) lg:hidden"
			>
				<MenuIcon className="size-5" aria-hidden />
			</SheetTrigger>
			<SheetContent
				side="top"
				className="mk border-0 bg-(--mk-white) p-0 shadow-none data-[side=top]:h-dvh"
			>
				<SheetTitle className="sr-only">Menu</SheetTitle>
				<nav
					aria-label="Main"
					className="mk-container flex flex-col pt-16"
				>
					{[{ label: "Home", href: "/" }, ...NAV_LINKS].map(
						(link, i) => (
							<a
								key={link.href}
								href={link.href}
								onClick={() => setOpen(false)}
								aria-current={
									pathname === link.href ? "page" : undefined
								}
								className={cn(
									"mk-pop border-(--mk-line) border-b py-4 font-semibold text-[26px] tracking-[-0.03em] text-(--mk-ink-2) aria-[current=page]:text-(--mk-ink)",
								)}
								style={{ animationDelay: `${i * 30}ms` }}
							>
								{link.label}
							</a>
						),
					)}
					<div className="mt-8 flex gap-3">
						<a
							href="/demo"
							className={mkButton({
								size: "lg",
								className: "flex-1",
							})}
						>
							Book a demo
						</a>
						<a
							href="/login"
							className={mkButton({
								variant: "secondary",
								size: "lg",
							})}
						>
							Sign in
						</a>
					</div>
				</nav>
			</SheetContent>
		</Sheet>
	);
}
